import type {
  LocationRecordModel,
  ProductCategoriesRecord,
  ProductsRecord,
  ProductSubCategoriesRecord,
  SuppliersRecord,
  ReportingGroupsRecord,
} from "@/utils/PocketBaseAdapter";
import { pb } from "@/utils/PocketBaseAdapter";
import { defineStore } from "pinia";
import { useUserStore } from "@/utils/UserStore";

export const useProductStore = defineStore("ProductStore", {
  state: () => ({
    categories: new Map<string, ProductCategoriesRecord>(),
    subCategories: new Map<string, ProductSubCategoriesRecord>(),
    products: [] as ProductsRecord[],
    categoryList: [] as ProductCategoriesRecord[],
    subCategoryList: [] as ProductSubCategoriesRecord[],
    allSubCategoryList: [] as ProductSubCategoriesRecord[],
    locationList: [] as LocationRecordModel[],
    suppliersList: [] as SuppliersRecord[],
    reportingGroupList: [] as ReportingGroupsRecord[],
    collectionId: "" as string,
  }),
  getters: {
    productMapData(state) {
      return state.products.reduce(
        (map, product) => {
          map[product.id] = product;
          return map;
        },
        {} as Record<number, (typeof state.products)[0]>,
      );
    },
  },
  actions: {
    // 👉 fetch reportingGroups
    async fetchReportingGroups() {
      const reportingGroupPromise = pb.collection("reportingGroups").getFullList<ReportingGroupsRecord>();
      this.reportingGroupList = await reportingGroupPromise;
    },

    // 👉 fetch suppliers
    async fetchSuppliers() {
      const supplierPromise = pb.collection("suppliers").getFullList<SuppliersRecord>();

      this.suppliersList = await supplierPromise;
    },

    async refreshStore(location?: string) {
      // this feels so wrong, but i'll leave this here - something to think about?
      // await pb.collection("productCategories").getFullList<ProductCategoriesRecord>(
      //    {
      //        expand: 'productSubCategories_via_category.products_via_subCategory'
      //    }
      // )
      location ??= useUserStore().currentLocation;

      this.categories.clear();
      this.subCategories.clear();

      const categoriesPromise = pb.collection("productCategories").getFullList<ProductCategoriesRecord>();
      const subCategoriesPromise = pb.collection("productSubCategories").getFullList<ProductSubCategoriesRecord>();

      (await categoriesPromise).forEach((cat) =>
        this.categories.set(cat.id, cat),
      );
      (await subCategoriesPromise).forEach((subcat) =>
        this.subCategories.set(subcat.id, subcat),
      );

      const productsPromise = pb
        .collection("products")
        .getFullList<ProductsRecord>({
          filter: `location = "${location}" && inactive = false`,
          expand: "location,category, category.TODPricing,subCategory, subCategory.TODPricing, productsOptions_via_product, productsOptions_via_product.optionGroup, productsOptions_via_product.options",
        });

      this.products = await productsPromise;
    },
    async fetchProductCategories() {
      try {
        const location = useUserStore().currentLocation;
        const categoriesPromise = await pb
          .collection("productCategories")
          .getFullList<ProductCategoriesRecord>({
            sort: "sortOrder",
            filter: `location = "${location}"`,
          });

        this.categoryList = categoriesPromise;
      } catch (error) {
        console.log("Error getting product categories: ", error);
      }
    },
    async fetchProducts(location?: string, params?: any) {
      location ??= useUserStore().currentLocation;

      let filters = `location = "${location}"`;

      if (params?.category) {
        if (filters) filters += `&& category="${params?.category}"`;
        else filters = `category="${params?.category}"`;
      }
      if (params?.subCategory) {
        if (filters) filters += `&& subCategory="${params?.subCategory}"`;
        else filters = `subCategory="${params?.subCategory}"`;
      }
      if (params?.q) {
        if (filters)
          filters += `&& (description ~ "${params.q}" || name ~ "${params.q}" || barcode ~ "${params.q}")`;
        else filters = `description ~ "${params.q}" || name ~ "${params.q}" || barcode ~ "${params.q}"`;
      }

      let sortParam = "";
      if (params?.sortBy.length) {
        sortParam =
          params?.sortBy[0].order === "desc"
            ? `-${params?.sortBy[0].key}`
            : params?.sortBy[0].key;
      }

      const productsPromise = await pb.collection("products").getList<ProductsRecord>(params.page, params.itemsPerPage, {
          filter: filters,
          sort: sortParam || "",
          expand: "location,category,subCategory,productsOptions_via_product",
          requestKey: null,
        });

      this.collectionId = productsPromise?.items?.[0]?.collectionId;

      return productsPromise;
    },
    async getSubcategories(category: string) {
      if (!category) return false;

      const subCategoriesPromise = await pb
        .collection("productSubCategories")
        .getFullList<ProductSubCategoriesRecord>({
          filter: `category = "${category}"`,
        });

      this.subCategoryList = subCategoriesPromise;
    },
    async getAllSubcategories() {
      const allSubCategoriesPromise = await pb
        .collection("productSubCategories")
        .getFullList<ProductSubCategoriesRecord>({
          sort: "sortOrder",
          expand: "category",
        });

      this.allSubCategoryList = allSubCategoriesPromise;
    },
  },
});
